<template>
  <v-form ref="form" v-model="valid">
    <v-row dense>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.code"
          outlined
          dense
          placeholder="VD: IT_EQUIP, FURNITURE, MC"
          :rules="[v => !!v || 'Vui lòng nhập mã đầu mục']"
        >
          <template v-slot:label>
            Mã đầu mục <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="formData.name"
          outlined
          dense
          placeholder="VD: Máy chiếu, Thiết bị Tin học, Bàn ghế"
          :rules="[v => !!v || 'Vui lòng nhập tên đầu mục']"
        >
          <template v-slot:label>
            Tên đầu mục <span class="red--text font-weight-bold">*</span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <!-- Đơn vị tính (dạng tags - có thể nhập nhiều đơn vị) -->
        <v-combobox
          v-model="formData.units"
          :items="suggestedUnits"
          label="Đơn vị tính (có thể nhập nhiều)"
          outlined
          dense
          clearable
          multiple
          chips
          small-chips
          deletable-chips
          placeholder="Nhập đơn vị rồi ấn Enter (Cái, Chiếc, Bộ, Hộp...)"
        />
      </v-col>
      <v-col cols="12" md="6">
        <!-- Người dùng có thể tự do nhập bất kỳ tags nào và ấn Enter -->
        <v-combobox
          v-model="formData.tags"
          :items="suggestedTags"
          label="Tags (Nhãn đầu mục)"
          outlined
          dense
          clearable
          multiple
          chips
          small-chips
          deletable-chips
          placeholder="Nhập tag rồi ấn Enter (VD: Cấp 1, Thiết bị số...)"
        />
      </v-col>
      <v-col cols="12" md="12">
        <v-autocomplete
          v-model="formData.parent"
          :items="parentCategories"
          item-text="name"
          item-value="id"
          label="Đầu mục cha (nếu có)"
          outlined
          dense
          clearable
          placeholder="Chọn đầu mục cha"
        >
          <template v-slot:item="{ item }">
            <v-icon small color="primary" class="mr-2">mdi-folder</v-icon>
            <span>{{ item.name }}</span>
            <span class="grey--text ml-2">({{ item.code }})</span>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="12">
        <v-textarea
          v-model="formData.description"
          label="Ghi chú mô tả"
          outlined
          dense
          rows="3"
          placeholder="Nhập mô tả / ghi chú cho đầu mục..."
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { AssetCategory } from '@/plugins/api'

export default {
  name: 'CategoryForm',
  props: {
    categoryData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      valid: true,
      allCategories: [],
      suggestedUnits: ['Cái', 'Chiếc', 'Bộ', 'Hộp', 'Thùng', 'Lít', 'Kg', 'Ram', 'Cuộn', 'Mét', 'Bình', 'Chai', 'Gói', 'Quyển', 'Cuốn'],
      suggestedTags: ['Cấp 1', 'Cấp 2', 'Cấp 3', 'Thiết bị tin học', 'Đồ gỗ', 'Phòng học'],
      formData: {
        id: null,
        code: '',
        name: '',
        units: [],
        tags: [],
        parent: null,
        description: ''
      }
    }
  },
  computed: {
    parentCategories() {
      return this.allCategories.filter(c => !this.formData.id || c.id !== this.formData.id)
    }
  },
  watch: {
    categoryData: {
      immediate: true,
      handler(val) {
        if (val && val.id) {
          let tags = []
          if (Array.isArray(val.tags)) {
            tags = val.tags
          } else if (typeof val.tags === 'string' && val.tags) {
            tags = val.tags.includes(',') ? val.tags.split(',').map(t => t.trim()).filter(Boolean) : [val.tags]
          } else if (val.academicLevel) {
            const alTitle = (val.academicLevel && (val.academicLevel.title || val.academicLevel.name)) || val.academicLevel
            if (alTitle) tags = [alTitle]
          }

          let units = []
          if (Array.isArray(val.units)) {
            units = val.units
          } else if (typeof val.units === 'string' && val.units) {
            units = val.units.includes(',') ? val.units.split(',').map(u => u.trim()).filter(Boolean) : [val.units]
          }

          this.formData = {
            id: val.id,
            code: val.code || '',
            name: val.name || '',
            units,
            tags,
            parent: (val.parent && val.parent.id) || val.parent || null,
            description: val.description || ''
          }
        } else {
          this.formData = {
            id: null,
            code: '',
            name: '',
            units: [],
            tags: [],
            parent: null,
            description: ''
          }
        }
        this.loadCategories()
      }
    }
  },
  async created() {
    await this.loadCategories()
  },
  methods: {
    async loadCategories() {
      try {
        const res = await AssetCategory.search({ _limit: -1, isDelete: false })
        this.allCategories = res || []
        // Extract all existing tags and units for suggestions
        const existingTags = new Set(this.suggestedTags)
        const existingUnits = new Set(this.suggestedUnits)
        ;(res || []).forEach(cat => {
          if (Array.isArray(cat.tags)) {
            cat.tags.forEach(t => existingTags.add(t))
          } else if (typeof cat.tags === 'string' && cat.tags) {
            cat.tags.split(',').forEach(t => existingTags.add(t.trim()))
          }

          if (Array.isArray(cat.units)) {
            cat.units.forEach(u => existingUnits.add(u))
          } else if (typeof cat.units === 'string' && cat.units) {
            cat.units.split(',').forEach(u => existingUnits.add(u.trim()))
          }
        })
        this.suggestedTags = Array.from(existingTags).filter(Boolean)
        this.suggestedUnits = Array.from(existingUnits).filter(Boolean)
      } catch (e) {
        console.error(e)
      }
    },
    validate() {
      return this.$refs.form.validate()
    },
    getData() {
      return {
        ...this.formData,
        units: Array.isArray(this.formData.units) ? this.formData.units.filter(Boolean) : (this.formData.units ? [this.formData.units] : []),
        tags: Array.isArray(this.formData.tags) ? this.formData.tags.filter(Boolean) : (this.formData.tags ? [this.formData.tags] : [])
      }
    },
    resetValidation() {
      if (this.$refs.form) this.$refs.form.resetValidation()
    }
  }
}
</script>
